import * as React from "react";
import { Box, Stack, Typography } from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import StarOutlineIcon from "@mui/icons-material/StarOutline";

interface Props {
  /**
   * The rating value (0-5, supports decimals like 3.5)
   */
  rating: number;
  /**
   * Maximum number of stars to display (default: 5)
   */
  maxStars?: number;
  /**
   * Size of the stars
   */
  size?: "small" | "medium" | "large";
  /**
   * Whether to show the numeric rating next to the stars
   */
  showNumeric?: boolean;
  /**
   * Color of the filled stars
   */
  color?: "primary" | "secondary" | "warning" | "inherit";
  /**
   * Additional styling
   */
  sx?: object;
}

const StarRating: React.FC<Props> = ({
  rating,
  maxStars = 5,
  size = "medium",
  showNumeric = false,
  color = "warning",
  sx
}) => {
  // Clamp rating between 0 and maxStars
  const clampedRating = Math.max(0, Math.min(maxStars, rating));

  const stars = [];

  for (let i = 1; i <= maxStars; i++) {
    const difference = clampedRating - i;

    if (difference >= 0) {
      // Full star
      stars.push(
        <StarIcon key={i} color={color} fontSize={size} sx={{ color: theme => theme.palette.warning.main }} />
      );
    } else if (difference > -1) {
      // Half star (when the difference is between -1 and 0)
      stars.push(
        <StarHalfIcon key={i} color={color} fontSize={size} sx={{ color: theme => theme.palette.warning.main }} />
      );
    } else {
      // Empty star
      stars.push(<StarOutlineIcon key={i} color="disabled" fontSize={size} />);
    }
  }

  return (
    <Stack direction="row" alignItems="center" spacing={1} sx={sx} data-testid="star-rating" data-rating={rating}>
      <Box display="flex" alignItems="center" sx={{ gap: 0.25 }}>
        {stars}
      </Box>
      {showNumeric && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontWeight: 500,
            minWidth: "fit-content" // Prevent text wrapping on small screens
          }}
        >
          ({rating.toFixed(1)})
        </Typography>
      )}
    </Stack>
  );
};

export { StarRating };
